// Configuration automatique de l'application selon l'environnement

interface AppConfig {
    baseUrl: string;
    apiBaseUrl: string;
    environment: 'development' | 'production' | 'local';
    csrfRefreshInterval: number;
    sessionWarningTime: number;
    debugMode: boolean;
}

function detectEnvironment(): 'development' | 'production' | 'local' {
    const hostname = window.location.hostname;
    const pathname = window.location.pathname;

    if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('local')) {
        return 'local';
    } else if (hostname.includes('staging') || hostname.includes('dev')) {
        return 'development';
    } else {
        return 'production';
    }
}

function detectBaseUrl(): { baseUrl: string; apiBaseUrl: string } {
    const protocol = window.location.protocol;
    const host = window.location.host;
    const pathname = window.location.pathname;

    // Détecter si on est dans un sous-dossier (comme /soft_edu/public)
    if (pathname.includes('/soft_edu/public')) {
        const baseUrl = `${protocol}//${host}/soft_edu/public`;
        return {
            baseUrl,
            apiBaseUrl: baseUrl
        };
    } else if (pathname.includes('/soft_edu')) {
        const baseUrl = `${protocol}//${host}/soft_edu`;
        return {
            baseUrl,
            apiBaseUrl: baseUrl
        };
    } else {
        // URL normale (localhost:8000 ou domaine en production)
        const baseUrl = `${protocol}//${host}`;
        return {
            baseUrl,
            apiBaseUrl: baseUrl
        };
    }
}

function createAppConfig(): AppConfig {
    const environment = detectEnvironment();
    const { baseUrl, apiBaseUrl } = detectBaseUrl();

    return {
        baseUrl,
        apiBaseUrl,
        environment,
        csrfRefreshInterval: environment === 'local' ? 5 * 60 * 1000 : 30 * 60 * 1000, // 5 min en local, 30 min en prod
        sessionWarningTime: 5 * 60 * 1000, // 5 minutes avant expiration
        debugMode: environment !== 'production'
    };
}

// Exporter la configuration
export const appConfig = createAppConfig();

// Fonction pour logger uniquement en mode debug
export function debugLog(message: string, ...args: any[]) {
    if (appConfig.debugMode) {
        console.log(`[App Debug] ${message}`, ...args);
    }
}

// Fonction pour configurer axios avec la configuration détectée
export function configureAxios() {
    if (!window.axios) {
        console.error('[App Config] Axios not available');
        return;
    }

    // Configurer la base URL
    window.axios.defaults.baseURL = appConfig.apiBaseUrl;

    // Headers par défaut
    window.axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
    window.axios.defaults.headers.common['Accept'] = 'application/json';

    // Timeout plus long pour les environnements de développement
    window.axios.defaults.timeout = appConfig.environment === 'local' ? 30000 : 10000;

    debugLog('Axios configured', {
        baseURL: window.axios.defaults.baseURL,
        timeout: window.axios.defaults.timeout,
        environment: appConfig.environment
    });
}

// Fonction pour corriger les URLs relatives selon l'environnement
export function resolveUrl(path: string): string {
    if (path.startsWith('http')) {
        return path; // URL absolue, pas de modification
    }

    // Enlever le slash initial si présent
    const cleanPath = path.startsWith('/') ? path.substring(1) : path;

    return `${appConfig.baseUrl}/${cleanPath}`;
}

// Fonction pour gérer les redirections selon l'environnement
export function redirectTo(path: string, replace: boolean = false) {
    const fullUrl = resolveUrl(path);

    debugLog('Redirecting to', fullUrl);

    if (replace) {
        window.location.replace(fullUrl);
    } else {
        window.location.href = fullUrl;
    }
}

// Exporter pour utilisation dans d'autres composants
export default appConfig;
